import { supabase } from '../lib/supabase';
import { subscribePooled } from '../utils/realtimePoolManager';

/**
 * PaystackWebhookSyncService
 * Provides real-time subscription to Paystack webhook events and verification logs.
 * Uses realtimePoolManager for connection pooling and debounced batch updates.
 */

// ─── Fetch recent webhook events ──────────────────────────────────────────────
export const fetchWebhookEvents = async ({ limit = 50, eventType = null } = {}) => {
  let query = supabase?.from('paystack_webhook_events')?.select('*')?.order('received_at', { ascending: false })?.limit(limit);
  if (eventType) query = query?.eq('event_type', eventType);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// ─── Fetch webhook event stats ─────────────────────────────────────────────────
export const fetchWebhookStats = async () => {
  const { data, error } = await supabase?.from('paystack_webhook_events')?.select('event_type, processing_status');
  if (error) throw error;

  const stats = { total: data?.length || 0, processed: 0, failed: 0, ignored: 0, byType: {} };
  data?.forEach(evt => {
    if (evt?.processing_status === 'processed') stats.processed++;
    else if (evt?.processing_status === 'failed') stats.failed++;
    else if (evt?.processing_status === 'ignored') stats.ignored++;
    if (!stats?.byType?.[evt?.event_type]) stats.byType[evt.event_type] = 0;
    stats.byType[evt.event_type]++;
  });
  return stats;
};

// ─── Subscribe to real-time webhook events (pooled) ───────────────────────────
export const subscribeToWebhookEvents = (onEvent) => {
  return subscribePooled({
    channelName: 'paystack:webhook-events',
    listeners: [{ event: 'INSERT', schema: 'public', table: 'paystack_webhook_events' }],
    debounceMs: 300,
    callback: (batch) => {
      batch?.forEach((payload) => {
        onEvent?.({ type: 'new_event', data: payload?.new });
      });
    },
  });
};

// ─── Subscribe to real-time verification log changes (pooled) ─────────────────
export const subscribeToVerificationLogs = (onUpdate) => {
  return subscribePooled({
    channelName: 'paystack:verification-logs',
    listeners: [{ event: '*', schema: 'public', table: 'paystack_verification_logs' }],
    debounceMs: 300,
    callback: (batch) => {
      batch?.forEach((payload) => {
        onUpdate?.({ type: payload?.eventType, data: payload?.new || payload?.old });
      });
    },
  });
};

// ─── Subscribe to real-time payments changes (pooled) ────────────────────────
export const subscribeToPayments = (onUpdate) => {
  return subscribePooled({
    channelName: 'paystack:payments',
    listeners: [{ event: 'UPDATE', schema: 'public', table: 'payments' }],
    debounceMs: 300,
    callback: (batch) => {
      batch?.forEach((payload) => {
        onUpdate?.({ type: 'payment_updated', data: payload?.new });
      });
    },
  });
};

// ─── Subscribe to real-time escrow changes (pooled) ───────────────────────────
export const subscribeToEscrowTransactions = (onUpdate) => {
  return subscribePooled({
    channelName: 'paystack:escrow-transactions',
    listeners: [{ event: 'UPDATE', schema: 'public', table: 'escrow_transactions' }],
    debounceMs: 300,
    callback: (batch) => {
      batch?.forEach((payload) => {
        onUpdate?.({ type: 'escrow_updated', data: payload?.new });
      });
    },
  });
};

// ─── Event type labels ────────────────────────────────────────────────────────
export const WEBHOOK_EVENT_LABELS = {
  'charge.success':          { label: 'Paiement confirmé',     color: 'bg-emerald-100 text-emerald-700', icon: '✅' },
  'charge.dispute.create':   { label: 'Litige créé',           color: 'bg-rose-100 text-rose-700',       icon: '⚠️' },
  'charge.dispute.resolve':  { label: 'Litige résolu',         color: 'bg-sky-100 text-sky-700',         icon: '🛡️' },
  'refund.processed':        { label: 'Remboursement traité',  color: 'bg-orange-100 text-orange-700',   icon: '💸' },
  'refund.failed':           { label: 'Remboursement échoué',  color: 'bg-red-100 text-red-700',         icon: '❌' },
  'transfer.failed':         { label: 'Transfert échoué',      color: 'bg-red-100 text-red-700',         icon: '❌' },
  'transfer.reversed':       { label: 'Transfert annulé',      color: 'bg-yellow-100 text-yellow-700',   icon: '↩️' },
};

export const PROCESSING_STATUS_LABELS = {
  processed: { label: 'Traité',     color: 'bg-emerald-100 text-emerald-700' },
  failed:    { label: 'Échoué',     color: 'bg-red-100 text-red-700' },
  ignored:   { label: 'Ignoré',     color: 'bg-gray-100 text-gray-500' },
  pending:   { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
};
